import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { PageLayout } from '../../shared/PageLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { Settings, LogOut, Link as LinkIcon, Users, CheckCircle2, AlertCircle, AlertTriangle, User, Edit2, Key, Unlink } from 'lucide-react';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import { type LearnerAccount, type ParentAccount, type AccountsState, getLinkedParents } from '../../../accounts';
import { AppState } from '../../../app/types';
import { useLinkChild } from '../hooks/useLinkChild';
import { usePasswordChange } from '../../shared/usePasswordChange';
import { ConsentPanel } from '../components/ConsentPanel';
interface ParentPageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  viewingLearnerId: string | undefined;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
}
export function SettingsPage({
  accounts,
  activeAccount,
  linkedLearners,
  linkParentToLearnerById
}: ParentPageProps) {
  const {
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updatePassword,
    unlinkParentFromLearner
  } = useAppStore();
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  const {
    linkCode,
    setLinkCode,
    error,
    success,
    pendingLearner,
    warnings: linkWarnings,
    hasGatingWarning: linkHasGatingWarning,
    hasBlockingWarning: linkHasBlockingWarning,
    handleSubmit: handleLinkChild,
    handleConfirmLink,
    handleCancelLink
  } = useLinkChild({
    accounts,
    parentId: activeAccount.id,
    linkedLearners,
    linkParentToLearnerById
  });
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount.displayName);
  const [editEmail, setEditEmail] = useState(activeAccount.email || '');
  // Password change — shared hook
  const pw = usePasswordChange({
    accountId: activeAccount.id,
    passwordHash: activeAccount.passwordHash,
    role: 'parent',
    updatePassword
  });
  // Unlink state
  const [unlinkLearnerId, setUnlinkLearnerId] = useState<string | null>(null);
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    setIsEditingProfile(false);
  };
  const handleConfirmUnlink = () => {
    if (!unlinkLearnerId) return;
    const learnerId = unlinkLearnerId;
    const learner = linkedLearners.find(l => l.id === learnerId);
    const learnerName = learner?.displayName ?? 'Kind';
    unlinkParentFromLearner(activeAccount.id, learnerId);
    setUnlinkLearnerId(null);
    // D-P3: undo-toast mirroring the teacher pattern. Re-linking a parent is
    // idempotent — `linkParentToLearnerById` recreates the parent↔learner
    // relationship without disturbing the learner account itself.
    toast.success(`${learnerName} is ontkoppeld`, {
      action: {
        label: 'Ongedaan maken',
        onClick: () => {
          linkParentToLearnerById(activeAccount.id, learnerId);
          toast.success(`${learnerName} is weer gekoppeld.`);
        }
      },
      duration: 5000
    });
  };
  return <PageLayout title="Instellingen" description="Beheer je ouderaccount en gekoppelde kinderen" data-id="element-8289" data-mp="sk5wzirvougk">
      <div className="max-w-3xl space-y-6" data-id="element-8290" data-mp="dvrlkjgebxoe">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Jouw persoonlijke gegevens" icon={Settings} accentColor="bg-emerald-100 text-emerald-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount.displayName);
        setEditEmail(activeAccount.email || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-8292" data-mp="1bfmajmuezrqv">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-8293" data-mp="3ma07j8oexq7l" /> Bewerk
              </Button> : null} data-id="element-8291" data-mp="isi2f900yxav">
          <div className="space-y-6" data-id="element-8294" data-mp="24c50t9txzswh">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-8295" data-mp="24e7zei95uuct">
                <div data-id="element-8296" data-mp="1zcs9ocw1fzo3">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8297" data-mp="1zfo9k6rg4yf6">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-8298" data-mp="29ayf34h1znxp" />
                </div>
                <div data-id="element-8299" data-mp="1ue7amgsivm18">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8300" data-mp="1ueh268t7u191">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-8301" data-mp="1pm7hp8avdte5" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-8302" data-mp="2x0jinxr5166v">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-8303" data-mp="31z47l10xw3g0">
                    Annuleren
                  </Button>
                  <Button onClick={handleSaveProfile} variant="primary" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-8304" data-mp="3boxmaawdoyoh">
                    Opslaan
                  </Button>
                </div>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-8305" data-mp="2nz36bkgriu00">
                <div data-id="element-8306" data-mp="2t0irdcixx0ie">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8307" data-mp="2t128ws4ohmvw">
                    Naam
                  </label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-8308" data-mp="898irc3qxzsw">
                    {activeAccount.displayName}
                  </div>
                </div>
                <div data-id="element-8309" data-mp="1nrocl5l478bn">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8310" data-mp="1nt9930qzp1c5">
                    E-mailadres
                  </label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-8311" data-mp="1izeto9if38iv">
                    {activeAccount.email || <span className="text-slate-400 italic" data-id="element-8312" data-mp="194v70s1edgwn">
                        Niet ingesteld
                      </span>}
                  </div>
                </div>
              </div>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-emerald-100 text-emerald-600" action={!pw.isChangingPassword ? <Button onClick={() => pw.setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-8314" data-mp="1yc7ll28an7fw">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-8315" data-mp="23ara9ae9uxby" /> Wijzig
              </Button> : null} data-id="element-8313" data-mp="v53ku40q8u7k">
          {pw.isChangingPassword ? <form onSubmit={pw.handleChangePassword} className="space-y-4" data-id="element-8316" data-mp="1tkzc666yrm2q">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8317" data-mp="1tj4tkyj6061t">
                <div className="sm:col-span-2" data-id="element-8318" data-mp="1fkesl7t85m86">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8319" data-mp="1klc97fyl687k">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.currentPassword} onChange={e => pw.setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-8320" data-mp="15pd48e6rwo6n" />
                </div>
                <div data-id="element-8321" data-mp="34zv0kjvex1wh">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8322" data-mp="34ya42opjf8vz">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.newPassword} onChange={e => pw.setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-8323" data-mp="of971c5dhhu1" />
                </div>
                <div data-id="element-8324" data-mp="1c2r5fqj57e5z">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8325" data-mp="1c27nwaxemrsh">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.confirmPassword} onChange={e => pw.setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-8326" data-mp="1m2hxllk8dh2w" />
                </div>
              </div>

              {pw.passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-8327" data-mp="3iwcoi5tbqjk3">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-8328" data-mp="39o05ktw5r1vo" />
                  <p data-id="element-8329" data-mp="39q31fujzrp8b">{pw.passwordError}</p>
                </div>}

              {pw.passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-8330" data-mp="30nbotrfdqnf3">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-8331" data-mp="16lsykzv5zen" />
                  <p data-id="element-8332" data-mp="3sdy9cxsxa8ti">{pw.passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-8333" data-mp="3nn9klq8mnpy2">
                <Button type="button" onClick={() => pw.resetPasswordForm()} variant="secondary" data-id="element-8334" data-mp="3nloqg1q8690q">
                  Annuleren
                </Button>
                <Button type="submit" variant="primary" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-8335" data-mp="k0945lytkymr">
                  Wachtwoord opslaan
                </Button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-8336" data-mp="26v4abcvmx0g0">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Linked Children */}
        <SettingsSection title="Gekoppelde Kinderen" description="Beheer de kinderen die aan jouw account zijn gekoppeld" icon={Users} accentColor="bg-teal-100 text-teal-600" data-id="element-8337" data-mp="2gssip5m5y6gn">
          {linkedLearners.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-8338" data-mp="2ukymds84qkz7">
              {linkedLearners.map(learner => {
            const otherParents = getLinkedParents(accounts, learner.id).filter(p => p.id !== activeAccount.id);
            return <div key={learner.id} className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm hover:border-teal-200 transition-colors" data-id="element-8339" data-mp="14hespsxfzuue">
                    <div className="flex items-center justify-between" data-id="element-8340" data-mp="1e7rdnm965q12">
                      <div className="flex items-center gap-3" data-id="element-8341" data-mp="19eosa1ejpmvy">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg" data-id="element-8342" data-mp="1n6uxisg5u9gm">
                          {learner.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div data-id="element-8343" data-mp="1x3zjosa0a4xf">
                          <div className="font-bold text-slate-900" data-id="element-8344" data-mp="1x2yayuggpuiv">
                            {learner.displayName}
                          </div>
                          <div className="text-xs font-medium text-slate-500" data-id="element-8345" data-mp="2sci8mwlz682">
                            {learner.username}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-8346" data-mp="29gv283pgvpdj">
                        <div className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100" data-id="element-8347" data-mp="2ef67e14thbpf">
                          Gekoppeld
                        </div>
                        <button onClick={() => setUnlinkLearnerId(learner.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors" title="Ontkoppelen" data-id="element-8348" data-mp="24o1vjaafu6yt">
                          <Unlink className="w-4 h-4" data-id="element-8349" data-mp="1gro3p4q0izsa" />
                        </button>
                      </div>
                    </div>
                    {otherParents.length > 0 && <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-8350" data-mp="3b4nnla1feg5d">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-8351" data-mp="3g40ce0buxxal">
                          Ook gekoppeld
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-id="element-8352" data-mp="1fzej9w7fhed0">
                          {otherParents.map(p => <span key={p.id} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-8353" data-mp="170srdag2gmzk">
                              <User className="w-3 h-3" data-id="element-8354" data-mp="x4fy2dofob5c" />
                              {p.displayName}
                            </span>)}
                        </div>
                      </div>}
                  </div>;
          })}
            </div> : <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed mb-8" data-id="element-8355" data-mp="j9ndh0tos4rw">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mx-auto mb-3" data-id="element-8356" data-mp="9dkcvzehge72">
                <Users className="w-6 h-6" data-id="element-8357" data-mp="3vdq3n0p9zm70" />
              </div>
              <p className="text-slate-500 font-medium" data-id="element-8358" data-mp="48gm7xfckg90">
                Er zijn nog geen kinderen gekoppeld aan dit account.
              </p>
            </div>}

          <div className="pt-6 border-t border-slate-100" data-id="element-8359" data-mp="95gpk0ktb4wf">
            <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-8360" data-mp="e5l9y1oucjrx">
              <LinkIcon className="w-4 h-4 text-teal-500" data-id="element-8361" data-mp="3cos2vjzgoq39" />
              Nieuw kind koppelen
            </h4>
            <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-8362" data-mp="3hoeiu16ockfr">
              Voer de 6-cijferige koppelcode in die je van de docent hebt
              gekregen of die het kind in zijn/haar profiel kan vinden.
            </p>

            <form onSubmit={handleLinkChild} className="flex flex-col sm:flex-row gap-3" data-id="element-8363" data-mp="3reh8brel7g9c">
              <Input variant="admin" value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())} placeholder="Bijv. ABCDEF" className="uppercase font-mono tracking-wider" maxLength={6} data-id="element-8364" data-mp="2ywvf1s4tt2co" />
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto" data-id="element-8365" data-mp="30mig7d05c7nz">
                Koppelen
              </Button>
            </form>

            {error && <div className="mt-4 flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3.5 rounded-lg border border-rose-100" data-id="element-8366" data-mp="27xtkxulruwk5">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-rose-500" data-id="element-8367" data-mp="2hvhiesarii60" />
                <p className="font-medium" data-id="element-8368" data-mp="2ht4rvqqxuzhc">{error}</p>
              </div>}

            {success && <div className="mt-4 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3.5 rounded-lg border border-emerald-100" data-id="element-8369" data-mp="3tamn21ugeqs9">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-500" data-id="element-8370" data-mp="3oarwy0ii6589" />
                <p className="font-medium" data-id="element-8371" data-mp="3vu173vyu27yd">{success}</p>
              </div>}

            {/* Child preview before confirming link */}
            {pendingLearner && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`mt-4 p-4 border rounded-xl ${linkHasGatingWarning ? 'bg-amber-50 border-amber-200' : 'bg-teal-50 border-teal-200'}`} data-id="element-8372" data-mp="3muw8kpn9uem7">
                <div className="flex items-center gap-3 mb-3" data-id="element-8373" data-mp="9o4913gkhqy7">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${linkHasGatingWarning ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-600'}`} data-id="element-8374" data-mp="9nm6jp3baghu">
                    <User className="w-5 h-5" data-id="element-8375" data-mp="11d7hkvfhlngn" />
                  </div>
                  <div data-id="element-8376" data-mp="1671v1h4i167a">
                    <p className="font-bold text-slate-900" data-id="element-8377" data-mp="167lgh7tcycq2">
                      {pendingLearner.displayName}
                    </p>
                    <p className="text-sm text-slate-500" data-id="element-8378" data-mp="co4emttea1ks">
                      {pendingLearner.grade ? `Groep ${pendingLearner.grade}` : 'Groep nog niet ingesteld'}
                    </p>
                  </div>
                </div>

                {/* Structured linkage warnings from the unified validator. */}
                {linkWarnings.length > 0 && <ul className="mb-3 space-y-2" data-id="element-8379" data-mp="vfx9yuvxryvo">
                    {linkWarnings.map((w, idx) => {
                const isBlocking = w.severity === 'blocking';
                const Icon = isBlocking ? AlertCircle : AlertTriangle;
                const palette = isBlocking ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-amber-50 border-amber-300 text-amber-900';
                return <li key={`${w.type}-${idx}`} className={`flex items-start gap-2 p-3 border rounded-lg text-sm ${palette}`} data-id="element-8380" data-mp="1lp60mou1k8dl" data-mpi={idx}>
                          <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-8381" data-mp="1vmkb87taa2xz" />
                          <span className="font-medium" data-id="element-8382" data-mp="20dsioyb1ze8i">{w.message}</span>
                        </li>;
              })}
                  </ul>}

                <div className="flex gap-2 justify-end" data-id="element-8383" data-mp="25f82kn1hvepb">
                  <Button variant="secondary" onClick={handleCancelLink} className="text-sm" data-id="element-8384" data-mp="29d5tbuawy8ut">
                    Annuleren
                  </Button>
                  <Button onClick={handleConfirmLink} disabled={linkHasBlockingWarning} className={`text-sm font-bold ${linkHasBlockingWarning ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : linkHasGatingWarning ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}`} data-id="element-8385" data-mp="2jak8lql3orlj">
                    <CheckCircle2 className="w-4 h-4 mr-1" data-id="element-8386" data-mp="2y209trdir4yu" />
                    {linkHasGatingWarning ? 'Bevestig toch koppeling' : 'Bevestig koppeling'}
                  </Button>
                </div>
              </motion.div>}
          </div>
        </SettingsSection>

        {/* Toestemming — apart van het koppelen, want dit is een juridische
            verklaring en geen instelling. */}
        <ConsentPanel data-id="element-8387" data-mp="3lqtd68wh0f3h" />

        {/* Logout */}
        <SettingsSection title="Uitloggen" description="Log uit van je ouderaccount" icon={LogOut} accentColor="bg-rose-100 text-rose-600" className="bg-rose-50/30 border-rose-100" data-id="element-8388" data-mp="3ls4bht75cun7">
          <div className="flex justify-end" data-id="element-8389" data-mp="1veng0tmrk7hp">
            <Button variant="secondary" className="w-full sm:w-auto text-rose-600 border-rose-200 hover:bg-rose-50 hover:border-rose-300" onClick={nav.handleLogout} data-id="element-8390" data-mp="1vff6xr5f1h1n">
              <LogOut className="w-4 h-4 mr-2" data-id="element-8391" data-mp="1j2l1ibevmyg1" />
              Uitloggen
            </Button>
          </div>
        </SettingsSection>
      </div>

      <ConfirmDialog open={!!unlinkLearnerId} onCancel={() => setUnlinkLearnerId(null)} onConfirm={handleConfirmUnlink} title="Kind ontkoppelen" message={`Weet je zeker dat je jezelf wilt ontkoppelen van ${linkedLearners.find(l => l.id === unlinkLearnerId)?.displayName}? Je kunt daarna geen voortgang meer inzien.`} confirmLabel="Ja, ontkoppelen" cancelLabel="Annuleren" variant="danger" data-id="element-8392" data-mp="1a4siox9y58nw" />
    </PageLayout>;
}